import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';

/**
 * Props:
 *  - record: {
 *      exitID, empID, empName, exitType, noticeDate, lastWorkingDate,
 *      reason, docUrl, status, remarks, created_at, updated_at
 *    }
 *  - company: {
 *      name, address, phone, email, website, city, country
 *      // optional: hrName, hrTitle
 *    }
 */

const styles = StyleSheet.create({
    page: { padding: 36, fontSize: 11, lineHeight: 1.4, fontFamily: 'Helvetica' },
    headerBlock: { marginBottom: 16 },
    companyName: { fontSize: 16, fontWeight: 700 },
    companyMeta: { color: '#444' },
    title: { fontSize: 14, marginTop: 12, marginBottom: 8, textDecoration: 'underline' },
    row: { marginBottom: 8 },
    label: { fontWeight: 700 },
    para: { marginBottom: 8, textAlign: 'justify' },
    signatureBlock: { marginTop: 32 },
    small: { fontSize: 10, color: '#555' },
    divider: { marginVertical: 8, borderBottomWidth: 1, borderBottomColor: '#999', borderBottomStyle: 'solid' },
});

const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY') : '');

const SubjectLine = ({ type }) => (
    <Text style={styles.row}>
        <Text style={styles.label}>Subject:</Text>{' '}
        {type === 'Termination' ? 'Employment Termination Notice' : 'Acceptance of Resignation'}
    </Text>
);

const Body = ({ record, company }) => {
    const type = record?.exitType || 'Resignation';
    const empName = record?.empName || `Employee #${record?.empID ?? ''}`;
    const lwd = fmt(record?.lastWorkingDate);
    const notice = fmt(record?.noticeDate);
    const reason = record?.reason || '—';

    if (type === 'Termination') {
        return (
            <>
                <Text style={styles.para}>
                    Dear {empName},
                </Text>
                <Text style={styles.para}>
                    This letter serves as formal notice that your employment with {company?.name || 'the Company'}
                    is terminated effective end of day {lwd}. This decision has been reached after due
                    consideration of the matter referenced in your file and is in accordance with your
                    employment terms and the Company’s policies.
                </Text>
                <Text style={styles.para}>
                    Reason(s): {reason}
                </Text>
                <Text style={styles.para}>
                    You are required to complete handover of all company property, assets, credentials,
                    and in-progress work before the effective date. Please coordinate with your reporting
                    manager and the HR department for exit clearance, final settlement, and revocation of
                    system access.
                </Text>
                <Text style={styles.para}>
                    Your final settlement (including any payable dues, subject to statutory deductions)
                    will be processed upon completion of the exit formalities and return of company property.
                </Text>
                <Text style={styles.para}>
                    If you have questions regarding this decision or the exit process, please contact HR at
                    {` `}{company?.email || 'hr@example.com'}.
                </Text>
                <Text style={styles.para}>
                    We acknowledge your contributions to the organization and wish you well in your future endeavors.
                </Text>
            </>
        );
    }

    // Resignation
    return (
        <>
            <Text style={styles.para}>
                Dear {empName},
            </Text>
            <Text style={styles.para}>
                We acknowledge receipt of your resignation notice dated {notice}. This letter confirms acceptance
                of your resignation, with your last working day being {lwd}.
            </Text>
            <Text style={styles.para}>
                Reason (as stated): {reason}
            </Text>
            <Text style={styles.para}>
                Please ensure complete handover of all company property, access cards, equipment, and active
                assignments to your reporting manager. HR will guide you through the exit clearance and
                final settlement process.
            </Text>
            <Text style={styles.para}>
                We appreciate your efforts during your tenure with {company?.name || 'the Company'}. Wishing you success
                in your future endeavors.
            </Text>
        </>
    );
};

export default function ExitLetterPDF({ record, company }) {
    const today = fmt(new Date());
    const refNo = `EXIT-${record?.exitID ?? 'XXXX'}/${dayjs().format('YYYY')}`;
    const type = record?.exitType || 'Resignation';

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.headerBlock}>
                    <Text style={styles.companyName}>{company?.name || 'Your Company (Pvt) Ltd.'}</Text>
                    <Text style={styles.companyMeta}>
                        {(company?.address || 'Company Address')}{company?.city ? `, ${company.city}` : ''}{company?.country ? `, ${company.country}` : ''}
                    </Text>
                    <Text style={styles.companyMeta}>
                        {(company?.phone || 'Phone: +92-000-0000000')} | {(company?.email || 'hr@example.com')}
                        {company?.website ? ` | ${company.website}` : ''}
                    </Text>
                </View>

                {/* Meta */}
                <View style={styles.row}><Text>Date: {today}</Text></View>
                <View style={styles.row}><Text>Ref: {refNo}</Text></View>

                <Text style={styles.title}>
                    {type === 'Termination' ? 'Employment Termination Letter' : 'Resignation Acceptance Letter'}
                </Text>

                <SubjectLine type={type} />

                <View style={styles.divider} />

                {/* Body */}
                <Body record={record} company={company} />

                {/* Footer / Signature */}
                <View style={styles.signatureBlock}>
                    <Text>Regards,</Text>
                    <Text style={{ marginTop: 24 }}>{company?.hrName || 'HR Manager'}</Text>
                    <Text style={styles.small}>{company?.hrTitle || 'Human Resources'}</Text>
                    <Text style={styles.small}>{company?.name || 'Your Company (Pvt) Ltd.'}</Text>
                </View>

                <View style={{ marginTop: 24 }}>
                    <Text style={styles.small}>CC: Employee File | Reporting Manager | HR</Text>
                </View>
            </Page>
        </Document>
    );
}
